<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Badge } from '@drama/ui'
import { ExternalLink, RefreshCw, Film, Terminal, Check, Copy } from '@lucide/vue'
import PrepArtifactsView from './PrepArtifactsView.vue'
import PrepDiagnosticsView from './PrepDiagnosticsView.vue'

const props = defineProps({
  record: { type: Object, required: true },
  title: { type: String, required: true },
  scopeLabel: { type: String, required: true },
  statusLabel: { type: String, required: true },
  loadReference: { type: Function, default: null },
})
const emit = defineEmits(['refresh', 'open-submission'])
const { t } = useI18n()

const activeTab = ref('artifacts') // 'artifacts' | 'trace'
const copiedKey = ref('')

async function copyText(text, key) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(String(text))
    copiedKey.value = key
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = ''
    }, 2000)
  } catch {
    // clipboard fallback
  }
}

function shortHash(text, len = 10) {
  if (!text || typeof text !== 'string') return text
  if (text.length <= len + 4) return text
  return `${text.slice(0, len)}...`
}

const sourceHref = computed(() => {
  const run = props.record.run_id
  const branch = props.record.branch_id
  if (typeof run !== 'string' || typeof branch !== 'string' || !/^[a-z0-9_-]{1,64}$/i.test(run) || !/^[a-z0-9_-]{1,64}$/i.test(branch)) return ''
  return `/novel/runs/${encodeURIComponent(run)}?${new URLSearchParams({ branch_id: branch })}#story`
})

// Trace 计算
const trace = computed(() => props.record.trace?.schema === 'story-film-agent-trace/v1' && Array.isArray(props.record.trace.turns) ? props.record.trace : null)

const traceTurns = computed(() => {
  if (!trace.value?.turns) return []
  return trace.value.turns.map((turn, tIdx) => {
    const steps = (turn?.steps ?? []).map((step, sIdx) => ({
      id: `t${tIdx + 1}-s${sIdx + 1}`,
      turn: tIdx + 1,
      number: sIdx + 1,
      ...step,
    }))
    const hasFail = steps.some(s => s.status === 'failed' || s.status === 'error' || (s.exit_code != null && s.exit_code !== 0))
    return {
      turnNumber: tIdx + 1,
      stepCount: steps.length,
      hasFail,
      steps,
    }
  })
})

const traceSteps = computed(() => traceTurns.value.flatMap(tr => tr.steps))
const publicEvents = computed(() => ['codex_native_public_tools', 'codex_runner_public_tools'].includes(props.record.events?.source) && Array.isArray(props.record.events.items) ? props.record.events.items : null)

const sourceMaterial = computed(() => {
  const material = props.record.task?.source?.material
  return Array.isArray(material?.opening) || Array.isArray(material?.turns) ? material : null
})
const opening = computed(() => Array.isArray(sourceMaterial.value?.opening) ? sourceMaterial.value.opening : [])
const turns = computed(() => Array.isArray(sourceMaterial.value?.turns) ? sourceMaterial.value.turns : [])

const screenplay = computed(() => {
  const current = props.record.artifacts?.screenplay
  if (Array.isArray(current?.scenes)) return current
  return props.record.artifact_status === 'verified' && Array.isArray(props.record.screenplay?.scenes) ? props.record.screenplay : null
})
const screenplayFromCurrentArtifact = computed(() => Array.isArray(props.record.artifacts?.screenplay?.scenes))
const providerPlan = computed(() => props.record.artifacts?.provider_plan ?? null)
const planSegments = computed(() => Array.isArray(providerPlan.value?.segments) ? providerPlan.value.segments : [])
const references = computed(() => props.record.task?.source_manifest && Array.isArray(props.record.task.references) ? props.record.task.references : null)

const referenceUrls = ref({})
const referenceStates = ref({})
let referenceGeneration = 0
const linked = computed(() => Array.isArray(props.record.linked_submissions) ? props.record.linked_submissions : [])
const bindingKey = computed(() => props.record.acceptance_status === 'no_linked_submission' ? 'unlinkedFormal'
  : props.record.acceptance_status === 'linked_submission' ? 'linkedFormal'
    : props.record.acceptance_status === 'binding_inconsistent' ? 'preparationBindingInconsistent' : 'bindingUnavailable')

function safeValue(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return t('storyFilm.executionLog.notProvided')
  return String(value)
}

function launchLabel(type) {
  return t(type === 'desktop' ? 'storyFilmReview.preparationLaunchDesktop' : type === 'runner' ? 'storyFilmReview.preparationLaunchRunner' : 'storyFilmReview.preparationLaunchUnknown')
}

function phaseLabel(phase) {
  return t(phase === 'preflight' ? 'storyFilmReview.preparationPhasePreflight' : phase === 'production' ? 'storyFilmReview.preparationPhaseProduction' : 'storyFilmReview.preparationPhaseUnknown')
}

function releaseReferences() {
  for (const url of Object.values(referenceUrls.value)) URL.revokeObjectURL(url)
  referenceUrls.value = {}
  referenceStates.value = {}
}

async function loadReferences() {
  const generation = ++referenceGeneration
  releaseReferences()
  if (!references.value) return
  const items = references.value
  const counts = new Map()
  for (const item of items) counts.set(item?.index, (counts.get(item?.index) ?? 0) + 1)
  for (const [position, item] of items.entries()) {
    if (generation !== referenceGeneration) return
    if (!Number.isSafeInteger(item?.index) || item.index < 0 || counts.get(item.index) !== 1 || typeof item.sha256 !== 'string' || !/^[a-f0-9]{64}$/i.test(item.sha256)) {
      referenceStates.value = { ...referenceStates.value, [position]: 'storyFilmReview.preparationRefInvalid' }
      continue
    }
    if (!props.loadReference) {
      referenceStates.value = { ...referenceStates.value, [position]: 'storyFilmReview.preparationRefFailed' }
      continue
    }
    referenceStates.value = { ...referenceStates.value, [position]: 'storyFilmReview.loading' }
    try {
      const blob = await props.loadReference(item.index)
      if (generation !== referenceGeneration) return
      referenceUrls.value = { ...referenceUrls.value, [position]: URL.createObjectURL(blob) }
      referenceStates.value = { ...referenceStates.value, [position]: 'storyFilmReview.preparationRefAvailable' }
    } catch {
      if (generation !== referenceGeneration) return
      referenceStates.value = { ...referenceStates.value, [position]: 'storyFilmReview.preparationRefFailed' }
    }
  }
}

watch(() => props.record, () => { void loadReferences() }, { immediate: true })
onUnmounted(() => { referenceGeneration += 1; releaseReferences() })
</script>

<template>
  <div class="preparation-overview" data-testid="preparation-overview">
    <!-- 头部紧凑横幅 -->
    <header class="dense-prep-header">
      <div class="dense-header-left">
        <h2 class="dense-heading">{{ title }}</h2>
        <Badge variant="info" size="sm">{{ statusLabel }}</Badge>
        <Badge variant="neutral" size="sm">{{ scopeLabel }}</Badge>
        <Badge variant="neutral" size="sm">{{ launchLabel(record.launch_type) }}</Badge>
        <Badge variant="neutral" size="sm">{{ phaseLabel(record.phase_limit) }}</Badge>
      </div>
      <div class="dense-header-actions">
        <a v-if="sourceHref" :href="sourceHref" target="_blank" class="dense-action-link">
          <ExternalLink :size="13" />
          <span>{{ t('storyFilmReview.liveStoryLink') }}</span>
        </a>
        <Button size="sm" variant="secondary" @click="emit('refresh')">
          <RefreshCw :size="13" />
          <span>{{ t('visualGameAdmin.novelRuns.audit.refresh') }}</span>
        </Button>
      </div>
    </header>

    <!-- 紧凑单行元数据信息条 -->
    <section class="dense-meta-strip" data-testid="preparation-source">
      <div class="meta-field">
        <span class="m-k">{{ t('storyFilmReview.preparationRun') }}:</span>
        <code class="m-v" :title="record.run_id">{{ shortHash(record.run_id, 8) }}</code>
        <button v-if="record.run_id" type="button" class="micro-copy" @click="copyText(record.run_id, 'run_id')">
          <Check v-if="copiedKey === 'run_id'" :size="12" class="is-ok" />
          <Copy v-else :size="12" />
        </button>
      </div>
      <span class="meta-sep">/</span>

      <div class="meta-field">
        <span class="m-k">{{ t('storyFilmReview.preparationBranch') }}:</span>
        <code class="m-v" :title="record.branch_id">{{ shortHash(record.branch_id, 8) }}</code>
        <button v-if="record.branch_id" type="button" class="micro-copy" @click="copyText(record.branch_id, 'branch_id')">
          <Check v-if="copiedKey === 'branch_id'" :size="12" class="is-ok" />
          <Copy v-else :size="12" />
        </button>
      </div>
      <span class="meta-sep">/</span>

      <div class="meta-field">
        <span class="m-k">序列:</span>
        <span class="m-v font-mono">{{ safeValue(record.from_seq) }} → {{ safeValue(record.through_seq) }}</span>
      </div>
      <span class="meta-sep">/</span>

      <div class="meta-field">
        <span class="m-k">模型:</span>
        <span class="m-v">{{ safeValue(record.model?.provider) }}/{{ safeValue(record.model?.model) }}</span>
      </div>
      <span class="meta-sep">/</span>

      <div class="meta-field">
        <span class="m-k">版本:</span>
        <code class="m-v" :title="record.source_version">{{ shortHash(record.source_version, 10) }}</code>
        <button v-if="record.source_version" type="button" class="micro-copy" @click="copyText(record.source_version, 'source_version')">
          <Check v-if="copiedKey === 'source_version'" :size="12" class="is-ok" />
          <Copy v-else :size="12" />
        </button>
      </div>
    </section>

    <!-- 工作区审查与技术诊断 Tab 切换 -->
    <div class="prep-tab-bar">
      <button
        type="button"
        class="prep-tab-btn"
        :class="{ 'is-active': activeTab === 'artifacts' }"
        @click="activeTab = 'artifacts'"
      >
        <Film :size="14" />
        <span>剧本与生成计划审查</span>
      </button>
      <button
        type="button"
        class="prep-tab-btn"
        :class="{ 'is-active': activeTab === 'trace' }"
        @click="activeTab = 'trace'"
      >
        <Terminal :size="14" />
        <span>Agent 轨迹与技术诊断</span>
        <Badge v-if="traceSteps.length" size="xs" variant="neutral">{{ traceSteps.length }} 步骤</Badge>
      </button>
    </div>

    <!-- 模式 1: 业务成果审查 -->
    <PrepArtifactsView
      v-if="activeTab === 'artifacts'"
      :record="record"
      :screenplay="screenplay"
      :screenplay-from-current-artifact="screenplayFromCurrentArtifact"
      :provider-plan="providerPlan"
      :plan-segments="planSegments"
      :source-material="sourceMaterial"
      :opening="opening"
      :turns="turns"
      :references="references"
      :reference-urls="referenceUrls"
      :reference-states="referenceStates"
      :linked="linked"
      :binding-key="bindingKey"
      :copied-key="copiedKey"
      @copy-text="copyText"
      @load-references="loadReferences"
      @open-submission="emit('open-submission', $event)"
    />

    <!-- 模式 2: Agent 轨迹与技术诊断 -->
    <PrepDiagnosticsView
      v-else
      :record="record"
      :trace-steps="traceSteps"
      :trace-turns="traceTurns"
      :public-events="publicEvents"
    />
  </div>
</template>

<style scoped>
.preparation-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--drama-color-text);
  width: 100%;
}

.dense-prep-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
}

.dense-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dense-heading {
  margin: 0;
  font-size: var(--drama-font-size-md, 16px);
  font-weight: 700;
}

.dense-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dense-action-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-primary);
  text-decoration: none;
  padding: 4px 8px;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
}

.dense-meta-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 12px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  font-size: var(--drama-font-size-xs, 12px);
}

.meta-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.m-k {
  color: var(--drama-color-text-muted);
}

.m-v {
  color: var(--drama-color-text);
  font-weight: 500;
}

.font-mono {
  font-family: var(--drama-font-family-mono);
}

.meta-sep {
  color: var(--drama-color-border);
  user-select: none;
}

.micro-copy {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  color: var(--drama-color-text-muted);
  cursor: pointer;
  padding: 0 2px;
}

.is-ok {
  color: var(--drama-color-success);
}

.prep-tab-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 2px solid var(--drama-color-border);
  padding-bottom: 2px;
}

.prep-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  border-bottom: 2px solid transparent;
  margin-bottom: -4px;
  font-size: var(--drama-font-size-sm, 14px);
  font-weight: 500;
  color: var(--drama-color-text-muted);
  cursor: pointer;
  transition: all 0.15s ease;
}

.prep-tab-btn.is-active {
  color: var(--drama-color-primary);
  border-bottom-color: var(--drama-color-primary);
  font-weight: 600;
}
</style>
